import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCompany } from "./useCompany";
import analytics from "@/lib/segment";
import { generateAndUploadVehicleImage } from "@/lib/generateVehicleImage";

export interface Trip {
  id: string;
  user_id: string;
  company_id?: string;
  vehicle_id?: string;
  reservation_id?: string;
  guest_id?: string;
  guest_name?: string;
  turo_vehicle_name?: string;
  turo_vehicle_id?: string;
  vin?: string;
  trip_start?: string;
  trip_end?: string;
  start_date?: string;
  end_date?: string;
  pickup_location?: string;
  return_location?: string;
  trip_status?: string;
  check_in_odometer?: number;
  check_out_odometer?: number;
  distance_traveled?: number;
  trip_days?: number;
  trip_price?: number;
  boost_price?: number;
  discount_3_day?: number;
  discount_1_week?: number;
  discount_2_week?: number;
  discount_3_week?: number;
  discount_1_month?: number;
  discount_2_month?: number;
  discount_3_month?: number;
  discount_non_refundable?: number;
  discount_early_bird?: number;
  host_promotional_credit?: number;
  delivery?: number;
  excess_distance?: number;
  extras?: number;
  cancellation_fee?: number;
  additional_usage?: number;
  late_fee?: number;
  improper_return_fee?: number;
  airport_operations_fee?: number;
  airport_parking_credit?: number;
  tolls_tickets?: number;
  on_trip_ev_charging?: number;
  post_trip_ev_charging?: number;
  smoking?: number;
  cleaning?: number;
  fines_paid_to_host?: number;
  gas_reimbursement?: number;
  gas_fee?: number;
  other_fees?: number;
  sales_tax?: number;
  total_earnings?: number;
  revenue: number;
  turo_fees?: number;
  net_revenue?: number;
  status?: string;
  notes?: string;
  created_at: string;
  vehicles?: {
    name: string;
    image_url?: string | null;
  } | null;
}

export interface TripImportData {
  reservation_id: string;
  guest_name: string;
  turo_vehicle_name: string;
  turo_vehicle_display_name: string;
  turo_vehicle_id: string;
  vin: string;
  trip_start: string | null;
  trip_end: string | null;
  pickup_location: string;
  return_location: string;
  trip_status: string;
  check_in_odometer: number | null;
  check_out_odometer: number | null;
  distance_traveled: number | null;
  trip_days: number | null;
  trip_price: number | null;
  boost_price: number | null;
  discount_3_day: number | null;
  discount_1_week: number | null;
  discount_2_week: number | null;
  discount_3_week: number | null;
  discount_1_month: number | null;
  discount_2_month: number | null;
  discount_3_month: number | null;
  discount_non_refundable: number | null;
  discount_early_bird: number | null;
  host_promotional_credit: number | null;
  delivery: number | null;
  excess_distance: number | null;
  extras: number | null;
  cancellation_fee: number | null;
  additional_usage: number | null;
  late_fee: number | null;
  improper_return_fee: number | null;
  airport_operations_fee: number | null;
  airport_parking_credit: number | null;
  tolls_tickets: number | null;
  on_trip_ev_charging: number | null;
  post_trip_ev_charging: number | null;
  smoking: number | null;
  cleaning: number | null;
  fines_paid_to_host: number | null;
  gas_reimbursement: number | null;
  gas_fee: number | null;
  other_fees: number | null;
  sales_tax: number | null;
  total_earnings: number | null;
}

function parseCurrency(value: string): number | null {
  if (!value || value === "") return null;
  // Remove $ and commas, handle negative values like "- $18.72"
  const cleaned = value.replace(/[$,]/g, "").replace(/\s/g, "").replace("--", "-");
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

function parseInteger(value: string): number | null {
  if (!value || value === "") return null;
  const num = parseInt(value, 10);
  return isNaN(num) ? null : num;
}

function parseTuroDateTime(value: string): string | null {
  if (!value || value === "") return null;
  // Format: "2025-08-23 07:00 AM"
  const match = value.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{1,2}):(\d{2})\s+(AM|PM)$/i);
  if (!match) return null;
  
  const [, date, hourStr, minuteStr, ampm] = match;
  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);
  
  if (ampm.toUpperCase() === "PM" && hour !== 12) {
    hour += 12;
  } else if (ampm.toUpperCase() === "AM" && hour === 12) {
    hour = 0;
  }
  
  return `${date}T${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}:00`;
}

export function parseCSVRow(headers: string[], values: string[]): TripImportData {
  const getVal = (colName: string): string => {
    const idx = headers.findIndex(h => h.toLowerCase() === colName.toLowerCase());
    return idx >= 0 ? values[idx] || "" : "";
  };

  return {
    reservation_id: getVal("Reservation ID"),
    guest_name: getVal("Guest"),
    turo_vehicle_name: getVal("Vehicle"),
    turo_vehicle_display_name: getVal("Vehicle name"),
    turo_vehicle_id: getVal("Vehicle id"),
    vin: getVal("VIN"),
    trip_start: parseTuroDateTime(getVal("Trip start")),
    trip_end: parseTuroDateTime(getVal("Trip end")),
    pickup_location: getVal("Pickup location"),
    return_location: getVal("Return location"),
    trip_status: getVal("Trip status"),
    check_in_odometer: parseInteger(getVal("Check-in odometer")),
    check_out_odometer: parseInteger(getVal("Check-out odometer")),
    distance_traveled: parseInteger(getVal("Distance traveled")),
    trip_days: parseInteger(getVal("Trip days")),
    trip_price: parseCurrency(getVal("Trip price")),
    boost_price: parseCurrency(getVal("Boost price")),
    discount_3_day: parseCurrency(getVal("3-day discount")),
    discount_1_week: parseCurrency(getVal("1-week discount")),
    discount_2_week: parseCurrency(getVal("2-week discount")),
    discount_3_week: parseCurrency(getVal("3-week discount")),
    discount_1_month: parseCurrency(getVal("1-month discount")),
    discount_2_month: parseCurrency(getVal("2-month discount")),
    discount_3_month: parseCurrency(getVal("3-month discount")),
    discount_non_refundable: parseCurrency(getVal("Non-refundable discount")),
    discount_early_bird: parseCurrency(getVal("Early bird discount")),
    host_promotional_credit: parseCurrency(getVal("Host promotional credit")),
    delivery: parseCurrency(getVal("Delivery")),
    excess_distance: parseCurrency(getVal("Excess distance")),
    extras: parseCurrency(getVal("Extras")),
    cancellation_fee: parseCurrency(getVal("Cancellation fee")),
    additional_usage: parseCurrency(getVal("Additional usage")),
    late_fee: parseCurrency(getVal("Late fee")),
    improper_return_fee: parseCurrency(getVal("Improper return fee")),
    airport_operations_fee: parseCurrency(getVal("Airport operations fee")),
    airport_parking_credit: parseCurrency(getVal("Airport parking credit")),
    tolls_tickets: parseCurrency(getVal("Tolls & tickets")),
    on_trip_ev_charging: parseCurrency(getVal("On-trip EV charging")),
    post_trip_ev_charging: parseCurrency(getVal("Post-trip EV charging")),
    smoking: parseCurrency(getVal("Smoking")),
    cleaning: parseCurrency(getVal("Cleaning")),
    fines_paid_to_host: parseCurrency(getVal("Fines (paid to host)")),
    gas_reimbursement: parseCurrency(getVal("Gas reimbursement")),
    gas_fee: parseCurrency(getVal("Gas fee")),
    other_fees: parseCurrency(getVal("Other fees")),
    sales_tax: parseCurrency(getVal("Sales tax")),
    total_earnings: parseCurrency(getVal("Total earnings")),
  };
}

export function parseCSV(csvContent: string): { headers: string[]; rows: string[][] } {
  const lines = csvContent.split("\n").filter(line => line.trim());
  if (lines.length === 0) return { headers: [], rows: [] };

  // Parse CSV properly handling quoted values
  const parseLine = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseLine(lines[0]);
  const rows = lines.slice(1).map(line => parseLine(line));
  
  return { headers, rows };
}

// Normalize a vehicle name for fuzzy matching: lowercase, strip accents,
// collapse whitespace/hyphens, and remove a leading 4-digit year if present.
function normalizeName(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")   // strip accents
    .replace(/[_\s-]+/g, " ")          // collapse whitespace / hyphens
    .replace(/^\d{4}\s+/, "")          // strip leading year like "2024 "
    .trim();
}

// Helper to find vehicle ID by nickname first, then VIN
function getVehicleIdForTrip(
  row: TripImportData,
  nicknameToVehicle: Map<string, { id: string; vin: string | null; turo_vehicle_id: string | null }>,
  vinToVehicle: Map<string, { id: string; turo_vehicle_id: string | null }>
): string | null {
  // First, try matching by turo_vehicle_name (nickname) - normalized
  if (row.turo_vehicle_name) {
    const normalized = normalizeName(row.turo_vehicle_name);
    const nicknameMatch = nicknameToVehicle.get(normalized);
    if (nicknameMatch) {
      return nicknameMatch.id;
    }
  }
  
  // If no nickname match, try VIN
  if (row.vin) {
    const vinMatch = vinToVehicle.get(row.vin.toUpperCase());
    if (vinMatch) {
      return vinMatch.id;
    }
  }
  
  return null;
}

export const useTrips = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { company } = useCompany();
  const companyId = company?.id;

  const { data: trips, isLoading } = useQuery({
    queryKey: ["trips", companyId],
    queryFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("Not authenticated");

      let query = supabase.from("trips").select("*, vehicles(name, image_url)");
      
      if (companyId) {
        query = query.eq("company_id", companyId);
      } else {
        query = query.eq("user_id", userData.user.id);
      }

      const { data, error } = await query.order("trip_start", { ascending: false });
      if (error) throw error;
      return data as Trip[];
    },
  });

  const importTrips = useMutation({
    mutationFn: async (params: { importData: TripImportData[]; vehicleLimit?: number | null }) => {
      const { importData, vehicleLimit } = params;
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("Not authenticated");

      // Get all vehicles to match by nickname first, then VIN
      let vehiclesQuery = supabase.from("vehicles").select("id, name, vin, turo_vehicle_id, year, make, model, license_plate");
      if (companyId) {
        vehiclesQuery = vehiclesQuery.eq("company_id", companyId);
      } else {
        vehiclesQuery = vehiclesQuery.eq("user_id", userData.user.id);
      }
      const { data: vehicles } = await vehiclesQuery;
      
      const vinToVehicle = new Map<string, { id: string; turo_vehicle_id: string | null; year: number; make: string; model: string; license_plate: string | null }>();
      const nicknameToVehicle = new Map<string, { id: string; vin: string | null; turo_vehicle_id: string | null; year: number; make: string; model: string; license_plate: string | null }>();
      const existingVins = new Set<string>();
      vehicles?.forEach(v => {
        // Map by VIN for VIN-based matching
        if (v.vin) {
          const vinKey = v.vin.toUpperCase();
          vinToVehicle.set(vinKey, { id: v.id, turo_vehicle_id: v.turo_vehicle_id, year: v.year, make: v.make, model: v.model, license_plate: v.license_plate });
          existingVins.add(vinKey);
        }
        // Map by nickname (normalized) for nickname-based matching
        if (v.name) {
          nicknameToVehicle.set(normalizeName(v.name), { id: v.id, vin: v.vin, turo_vehicle_id: v.turo_vehicle_id, year: v.year, make: v.make, model: v.model, license_plate: v.license_plate });
        }
      });

      const currentVehicleCount = vehicles?.length || 0;

      // Auto-create or update vehicles from CSV data (keyed by VIN only)
      const vehicleDataFromCSV = new Map<string, { vin: string; turo_vehicle_id?: string; make?: string; model?: string; name?: string; year?: number; license_plate?: string }>();
      for (const row of importData) {
        // Only process rows with a VIN
        if (!row.vin) continue;
        const vinKey = row.vin.toUpperCase();
        
        // Parse make, model, and license plate from the "Vehicle" column
        // Format: "Dodge Caravan (CA #9PDY884)"
        let make = "";
        let model = "";
        let licensePlate: string | undefined;
        if (row.turo_vehicle_name) {
          const parts = row.turo_vehicle_name.split(" ");
          if (parts.length >= 1) make = parts[0];
          if (parts.length >= 2) model = parts[1];
          // Extract license plate from pattern like "(CA #9PDY884)" — 7 chars after "#"
          const plateMatch = row.turo_vehicle_name.match(/#([A-Za-z0-9]{7})/);
          if (plateMatch) {
            licensePlate = plateMatch[1];
          }
        }

        // Parse year from "Vehicle name" column (last word)
        // Format: "Dodge Grand Caravan 2013"
        let year: number | undefined;
        if (row.turo_vehicle_display_name) {
          const displayParts = row.turo_vehicle_display_name.trim().split(" ");
          const lastWord = displayParts[displayParts.length - 1];
          const firstWord = displayParts[0];
          if (lastWord?.match(/^\d{4}$/)) {
            year = parseInt(lastWord, 10);
          } else if (firstWord?.match(/^\d{4}$/)) {
            year = parseInt(firstWord, 10);
          }
        }

        // Build vehicle nickname as "Make Model Year"
        const nickname = year && make && model
          ? `${make} ${model} ${year}`
          : row.turo_vehicle_display_name || row.turo_vehicle_name || undefined;

        // Only set/update if we don't already have data, or if new data has a turo_vehicle_id
        const existingData = vehicleDataFromCSV.get(vinKey);
        if (!existingData || (row.turo_vehicle_id && !existingData.turo_vehicle_id)) {
          vehicleDataFromCSV.set(vinKey, {
            vin: row.vin,
            turo_vehicle_id: row.turo_vehicle_id || existingData?.turo_vehicle_id || undefined,
            make: make || existingData?.make,
            model: model || existingData?.model,
            name: nickname || existingData?.name || undefined,
            year: year || existingData?.year,
            license_plate: licensePlate || existingData?.license_plate,
          });
        }
      }

      // Pre-scan for nickname matches so they count as existing (not new)
      // This handles vehicles created by expense imports that have no VIN yet
      const nicknameMatchedVins = new Set<string>();
      for (const [vinKey, vData] of vehicleDataFromCSV) {
        if (existingVins.has(vinKey)) continue; // already matched by VIN
        const normalizedCSVName = vData.name ? normalizeName(vData.name) : null;
        if (normalizedCSVName && nicknameToVehicle.has(normalizedCSVName)) {
          nicknameMatchedVins.add(vinKey);
        }
      }

      // Find new VINs that need vehicle creation (exclude both VIN matches and nickname matches)
      const newVinsInCSV = Array.from(vehicleDataFromCSV.keys()).filter(
        vin => !existingVins.has(vin) && !nicknameMatchedVins.has(vin)
      );

      // Sort new VINs alphabetically by their vehicle name for consistent limit enforcement
      newVinsInCSV.sort((a, b) => {
        const nameA = vehicleDataFromCSV.get(a)?.name || a;
        const nameB = vehicleDataFromCSV.get(b)?.name || b;
        return nameA.localeCompare(nameB);
      });

      // Calculate how many new vehicles we can create based on limit
      let allowedNewVehicles = newVinsInCSV.length;
      let skippedVehicles = 0;
      const totalVinsInCSV = vehicleDataFromCSV.size;

      if (vehicleLimit !== null && vehicleLimit !== undefined) {
        const remainingSlots = Math.max(0, vehicleLimit - currentVehicleCount);
        allowedNewVehicles = Math.min(newVinsInCSV.length, remainingSlots);
        skippedVehicles = newVinsInCSV.length - allowedNewVehicles;
      }

      // Get the VINs we're allowed to process (existing + allowed new)
      const allowedNewVins = new Set(newVinsInCSV.slice(0, allowedNewVehicles));
      const processableVins = new Set([...existingVins, ...nicknameMatchedVins, ...allowedNewVins]);

      // Create or update vehicles using VIN as the sole identifier
      for (const [vinKey, vData] of vehicleDataFromCSV) {
        if (!processableVins.has(vinKey)) continue;
        
        const existingVehicle = vinToVehicle.get(vinKey);
        
        if (existingVehicle) {
          // Fill in any missing fields on the existing vehicle
          const updates: Record<string, unknown> = {};
          if (vData.turo_vehicle_id && vData.turo_vehicle_id !== existingVehicle.turo_vehicle_id) updates.turo_vehicle_id = vData.turo_vehicle_id;
          if (vData.year && (!existingVehicle.year || existingVehicle.year === new Date().getFullYear())) updates.year = vData.year;
          if (vData.make && vData.make !== "Unknown" && (!existingVehicle.make || existingVehicle.make === "Unknown")) updates.make = vData.make;
          if (vData.model && vData.model !== "Unknown" && (!existingVehicle.model || existingVehicle.model === "Unknown")) updates.model = vData.model;
          if (vData.license_plate && !existingVehicle.license_plate) updates.license_plate = vData.license_plate;
          if (Object.keys(updates).length > 0) {
            const { error: updateErr } = await supabase.from("vehicles").update(updates).eq("id", existingVehicle.id);
            if (updateErr) console.warn("Failed to update vehicle (VIN match):", updateErr.message);
          }
        } else {
          // Before creating, check if there's a nickname match (e.g. vehicle created by expense import without VIN)
          const normalizedCSVName = vData.name ? normalizeName(vData.name) : null;
          const nicknameMatch = normalizedCSVName ? nicknameToVehicle.get(normalizedCSVName) : null;

          if (nicknameMatch) {
            // Fill in missing fields on the nickname-matched vehicle
            const updates: Record<string, unknown> = {};
            if (!nicknameMatch.vin && vData.vin) updates.vin = vData.vin;
            if (vData.turo_vehicle_id && vData.turo_vehicle_id !== nicknameMatch.turo_vehicle_id) updates.turo_vehicle_id = vData.turo_vehicle_id;
            if (vData.year && (!nicknameMatch.year || nicknameMatch.year === new Date().getFullYear())) updates.year = vData.year;
            if (vData.make && vData.make !== "Unknown" && (!nicknameMatch.make || nicknameMatch.make === "Unknown")) updates.make = vData.make;
            if (vData.model && vData.model !== "Unknown" && (!nicknameMatch.model || nicknameMatch.model === "Unknown")) updates.model = vData.model;
            if (vData.license_plate && !nicknameMatch.license_plate) updates.license_plate = vData.license_plate;
            if (Object.keys(updates).length > 0) {
              const { error: updateErr } = await supabase.from("vehicles").update(updates).eq("id", nicknameMatch.id);
              if (updateErr) console.warn("Failed to update vehicle (nickname match):", updateErr.message);
            }
            vinToVehicle.set(vinKey, { id: nicknameMatch.id, turo_vehicle_id: vData.turo_vehicle_id || nicknameMatch.turo_vehicle_id, year: vData.year || nicknameMatch.year, make: vData.make || nicknameMatch.make, model: vData.model || nicknameMatch.model, license_plate: vData.license_plate || nicknameMatch.license_plate });
            existingVins.add(vinKey);
          } else {
            // Create new vehicle
            const newVehicle = {
              user_id: userData.user.id,
              company_id: companyId || null,
              name: vData.name || `${vData.make} ${vData.model}`.trim() || "Unknown Vehicle",
              year: vData.year || new Date().getFullYear(),
              make: vData.make || "Unknown",
              model: vData.model || "Unknown",
              vin: vData.vin,
              turo_vehicle_id: vData.turo_vehicle_id || null,
              license_plate: vData.license_plate || null,
            };

            const { data: createdVehicle, error: vehicleError } = await supabase
              .from("vehicles")
              .insert(newVehicle)
              .select("id")
              .single();

            if (!vehicleError && createdVehicle) {
              vinToVehicle.set(vinKey, { id: createdVehicle.id, turo_vehicle_id: vData.turo_vehicle_id || null, year: vData.year || newVehicle.year, make: newVehicle.make, model: newVehicle.model, license_plate: vData.license_plate || null });
              
              // Fire-and-forget: generate AI vehicle image
              generateAndUploadVehicleImage({
                vehicleId: createdVehicle.id,
                make: newVehicle.make,
                model: newVehicle.model,
                year: newVehicle.year,
                nickname: newVehicle.name,
              });
            }
          }
        }
      }

      // Get or create guests
      let guestsQuery = supabase.from("guests").select("id, name");
      if (companyId) {
        guestsQuery = guestsQuery.eq("company_id", companyId);
      } else {
        guestsQuery = guestsQuery.eq("user_id", userData.user.id);
      }
      const { data: existingGuests } = await guestsQuery;
      
      const guestNameToId = new Map<string, string>();
      existingGuests?.forEach(g => guestNameToId.set(g.name, g.id));

      // Find new guests to create
      const newGuestNames = new Set<string>();
      importData.forEach(row => {
        if (row.guest_name && !guestNameToId.has(row.guest_name)) {
          newGuestNames.add(row.guest_name);
        }
      });

      // Create new guests
      if (newGuestNames.size > 0) {
        const newGuests = Array.from(newGuestNames).map(name => ({
          user_id: userData.user.id,
          company_id: companyId || null,
          name,
        }));
        
        const { data: createdGuests, error: guestError } = await supabase
          .from("guests")
          .insert(newGuests)
          .select("id, name");
        
        if (guestError) throw guestError;
        createdGuests?.forEach(g => guestNameToId.set(g.name, g.id));
      }

      // Process trips - upsert by reservation_id
      // Only process trips for vehicles we're allowed to handle
      let skippedTrips = 0;
      const tripsToUpsert: any[] = [];

      for (const row of importData) {
        // Skip trips for VINs we couldn't process due to limit
        if (row.vin) {
          const vinKey = row.vin.toUpperCase();
          if (!processableVins.has(vinKey)) {
            skippedTrips++;
            continue;
          }
        }

        tripsToUpsert.push({
          user_id: userData.user.id,
          company_id: companyId || null,
          reservation_id: row.reservation_id,
          guest_id: row.guest_name ? guestNameToId.get(row.guest_name) : null,
          guest_name: row.guest_name,
          vehicle_id: getVehicleIdForTrip(row, nicknameToVehicle, vinToVehicle),
          turo_vehicle_name: row.turo_vehicle_name,
          turo_vehicle_id: row.turo_vehicle_id,
          vin: row.vin,
          trip_start: row.trip_start,
          trip_end: row.trip_end,
          start_date: row.trip_start ? row.trip_start.split("T")[0] : null,
          end_date: row.trip_end ? row.trip_end.split("T")[0] : null,
          pickup_location: row.pickup_location,
          return_location: row.return_location,
          trip_status: row.trip_status,
          check_in_odometer: row.check_in_odometer,
          check_out_odometer: row.check_out_odometer,
          distance_traveled: row.distance_traveled,
          trip_days: row.trip_days,
          trip_price: row.trip_price,
          boost_price: row.boost_price,
          discount_3_day: row.discount_3_day,
          discount_1_week: row.discount_1_week,
          discount_2_week: row.discount_2_week,
          discount_3_week: row.discount_3_week,
          discount_1_month: row.discount_1_month,
          discount_2_month: row.discount_2_month,
          discount_3_month: row.discount_3_month,
          discount_non_refundable: row.discount_non_refundable,
          discount_early_bird: row.discount_early_bird,
          host_promotional_credit: row.host_promotional_credit,
          delivery: row.delivery,
          excess_distance: row.excess_distance,
          extras: row.extras,
          cancellation_fee: row.cancellation_fee,
          additional_usage: row.additional_usage,
          late_fee: row.late_fee,
          improper_return_fee: row.improper_return_fee,
          airport_operations_fee: row.airport_operations_fee,
          airport_parking_credit: row.airport_parking_credit,
          tolls_tickets: row.tolls_tickets,
          on_trip_ev_charging: row.on_trip_ev_charging,
          post_trip_ev_charging: row.post_trip_ev_charging,
          smoking: row.smoking,
          cleaning: row.cleaning,
          fines_paid_to_host: row.fines_paid_to_host,
          gas_reimbursement: row.gas_reimbursement,
          gas_fee: row.gas_fee,
          other_fees: row.other_fees,
          sales_tax: row.sales_tax,
          total_earnings: row.total_earnings,
          revenue: row.total_earnings || 0,
          status: row.trip_status?.toLowerCase().includes("cancel") ? "cancelled" : "completed",
        });
      }

      // Batch upsert using composite unique index on (reservation_id, company_id)
      let insertedCount = 0;
      let updatedCount = 0;
      if (tripsToUpsert.length > 0) {
        // Upsert in batches of 100 to avoid payload limits
        const batchSize = 100;
        for (let i = 0; i < tripsToUpsert.length; i += batchSize) {
          const batch = tripsToUpsert.slice(i, i + batchSize);
          const { error } = await supabase
            .from("trips")
            .upsert(batch, { onConflict: "reservation_id,company_id" });
          if (error) throw error;
        }
        // We can't distinguish inserted vs updated with upsert, so report total
        insertedCount = tripsToUpsert.length;
        updatedCount = 0;
      }

      return { 
        insertedCount, 
        updatedCount, 
        skippedVehicles,
        skippedTrips,
        totalVinsInCSV,
        vehiclesImported: allowedNewVehicles + (vehicleDataFromCSV.size - newVinsInCSV.length),
      };
    },
    onSuccess: ({ insertedCount, updatedCount, skippedVehicles, totalVinsInCSV, vehiclesImported }) => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      
      // Track earnings import
      analytics.track("Earnings CSV imported", {
        row_count: insertedCount + updatedCount,
        num_unique_vehicles_in_csv: totalVinsInCSV,
      });

      // Track if vehicles were skipped due to limit
      if (skippedVehicles > 0) {
        analytics.track("Too Many Vehicles", {
          num_vehicles: totalVinsInCSV,
          num_vehicles_allowed: vehiclesImported,
          plan_type: "current", // Will be enriched by Segment with user traits
        });
      }
      
      if (skippedVehicles === 0) {
        toast({
          title: "Import successful",
          description: `${insertedCount} trips imported.`,
        });
      }
      // If vehicles were skipped, the Earnings component will show a custom message
    },
    onError: (error) => {
      toast({
        title: "Import failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const clearAllTrips = useMutation({
    mutationFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("Not authenticated");

      let query = supabase.from("trips").delete();
      
      if (companyId) {
        query = query.eq("company_id", companyId);
      } else {
        query = query.eq("user_id", userData.user.id);
      }

      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      
      analytics.track("All earnings cleared");
      
      toast({
        title: "Earnings cleared",
        description: "All trip data has been removed. You can re-import from Turo CSV files anytime.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to clear earnings",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    trips,
    isLoading,
    importTrips,
    clearAllTrips,
  };
};
